/**
 * Presentation metadata for raw Jira values (issue type names, browse URLs).
 * Shared by every surface that renders a ticket row — the release Items tab and the
 * Jira issue picker — so a ticket looks the same wherever it appears.
 */

import appConfig from '@/../appConfig';

/**
 * Fields the API might use to hand back a browsable Jira URL. `self` is deliberately
 * accepted only when it is not a REST endpoint — a Jira `self` normally points at
 * `/rest/api/...`, which is not a page a user should be sent to. When the payload carries
 * none of these, the link is assembled from the configured site URL instead — see jiraBrowseUrl.
 */
const URL_FIELDS = ['browseUrl', 'url', 'jiraUrl', 'link', 'self'];

/** Fallback used for any issue type Jira reports that is not in the table below. */
const DEFAULT_TYPE_META = { icon: 'mdi-file-outline', color: 'grey' };

/**
 * Lower-cased Jira issue type name -> icon + colour. Keys cover the default Jira
 * Software / Service Management type names; anything else falls back to the default.
 * Colours are Vuetify colour names so they follow the active theme.
 */
const TYPE_META = {
  bug: { icon: 'mdi-bug', color: 'error' },
  defect: { icon: 'mdi-bug', color: 'error' },
  story: { icon: 'mdi-bookmark', color: 'success' },
  'user story': { icon: 'mdi-bookmark', color: 'success' },
  task: { icon: 'mdi-check-circle-outline', color: 'info' },
  'sub-task': { icon: 'mdi-subdirectory-arrow-right', color: 'info' },
  subtask: { icon: 'mdi-subdirectory-arrow-right', color: 'info' },
  epic: { icon: 'mdi-lightning-bolt', color: 'purple' },
  improvement: { icon: 'mdi-arrow-up-bold-circle-outline', color: 'teal' },
  enhancement: { icon: 'mdi-arrow-up-bold-circle-outline', color: 'teal' },
  'new feature': { icon: 'mdi-star-outline', color: 'amber darken-2' },
  feature: { icon: 'mdi-star-outline', color: 'amber darken-2' },
  spike: { icon: 'mdi-magnify', color: 'blue-grey' },
  incident: { icon: 'mdi-alert-octagon-outline', color: 'error' },
  support: { icon: 'mdi-lifebuoy', color: 'blue-grey' },
  'service request': { icon: 'mdi-lifebuoy', color: 'blue-grey' },
  change: { icon: 'mdi-swap-horizontal', color: 'deep-purple' },
};

/**
 * @param {string} type raw Jira issue type name
 * @returns {{ icon: string, color: string }} icon + Vuetify colour for that type
 */
export function issueTypeMeta(type) {
  if (typeof type !== 'string') return DEFAULT_TYPE_META;
  return TYPE_META[type.trim().toLowerCase()] || DEFAULT_TYPE_META;
}

/**
 * Builds a browse link from the configured Jira site URL.
 * `VUE_APP_JIRA_BASE_URL` is the site root (e.g. `https://acme.atlassian.net`); a value that
 * already ends in `/browse` is accepted too. Unset or non-http config yields '' — the key then
 * renders as plain text rather than as a link that 404s.
 * @param {string} key Jira issue key, e.g. `AVA-471`
 * @returns {string}
 */
export function jiraBrowseUrl(key) {
  if (typeof key !== 'string' || !key.trim()) return '';
  const base = (appConfig.JIRA_BASE_URL || '').trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(base)) return '';
  const root = /\/browse$/i.test(base) ? base : `${base}/browse`;
  return `${root}/${encodeURIComponent(key.trim())}`;
}

/**
 * Resolves a browsable Jira link for one issue / release item. A URL supplied by the API always
 * wins; when the payload carries none (the release-items endpoint currently returns only the
 * key) the link is assembled from the configured site URL.
 * @param {object} source raw issue / release item
 * @param {string} [key] issue key to fall back on, when the caller has already normalised it
 * @returns {string} an http(s) link to the ticket, or '' when neither source yields one
 */
export function readJiraUrl(source, key = '') {
  for (const field of URL_FIELDS) {
    const raw = source ? source[field] : null;
    if (typeof raw !== 'string') continue;
    const value = raw.trim();
    if (!/^https?:\/\//i.test(value)) continue;
    if (value.toLowerCase().includes('/rest/')) continue;
    return value;
  }
  const fallbackKey = key || (source ? source.jiraKey || source.issueKey || source.key : '');
  return jiraBrowseUrl(fallbackKey);
}
